import moment from 'moment-timezone';
import Booking from '@solvhealth/types/interfaces/Booking';
import {
  TYPE_SELF,
  TYPE_SKIP,
  ORIGIN_BOOKING_WIDGET,
  ORIGIN_KIOSK_SIGN_IN,
  ORIGIN_QUEUE,
  BOOKING_CANCELLATION_REASON_TRANSFERRED,
  UBER_RIDE_REQUESTED,
  ASAP_VALUE,
  BookingStatus,
  TELEMED_IN_PROGRESS,
} from '../../constants/index';
import { isEmptyString } from '../../core/util/string';

const isRescheduledBooking = (booking: any) =>
  booking.hasBeenRescheduled === true || !isEmptyString(booking.rescheduledTo);

const isBookingWidgetBooking = (booking: any) => booking.origin === ORIGIN_BOOKING_WIDGET;

const isWalkIn = (origin: any) => origin === ORIGIN_KIOSK_SIGN_IN || origin === ORIGIN_QUEUE;

const upcomingStatuses = [
  BookingStatus.PENDING,
  BookingStatus.RESERVED,
  BookingStatus.CONFIRMED,
  BookingStatus.CHECKED_IN,
  BookingStatus.IN_EXAM_ROOM,
];

const isUpcoming = (booking: any) => upcomingStatuses.includes(booking.status);

const hasInsuranceInfo = (booking: any) => booking.hasInsuranceInfo;

const isSelfPay = (booking: any) =>
  booking.insurerType === TYPE_SELF || booking.insurer_type === TYPE_SELF;

const isInsuranceSkipped = (booking: any) =>
  booking.insurerType === TYPE_SKIP || booking.insurer_type === TYPE_SKIP;

const isInsuranceTypeUnset = (booking: any) =>
  isEmptyString(booking.insurerType) || isEmptyString(booking.insurer_type);

const isCallAhead = (booking: any) => booking.isCallAhead;

const isRescheduled = (booking: any) =>
  booking.status !== BookingStatus.CANCELLED && isRescheduledBooking(booking);

const isCancelled = (booking: any) => booking.status === BookingStatus.CANCELLED;

const isDischarged = (booking: any) => booking.status === BookingStatus.DISCHARGED;

const isNoShow = (booking: any) => booking.status === BookingStatus.NO_SHOW;

const isPending = (booking: any) => booking.status === BookingStatus.PENDING;

const isCheckedIn = (booking: any) => booking.status === BookingStatus.CONFIRMED;

const shouldShowRebookingScreen = (booking: any) =>
  isDischarged(booking) || isCancelled(booking) || isNoShow(booking);

const hasBeenTransferred = (booking: any) =>
  booking.status === BookingStatus.CANCELLED &&
  booking.cancellationReason === BOOKING_CANCELLATION_REASON_TRANSFERRED;

const isActiveBooking = (booking: any) =>
  !isPending(booking) && !isCancelled(booking) && !isRescheduledBooking(booking);

const isOldRescheduledFlow = (booking: any) =>
  !isEmptyString(booking.rescheduledTo) && booking.hasBeenRescheduled === false;

const hasRequestedUberRide = (booking: any) => booking.uberRideStatus === UBER_RIDE_REQUESTED;

const isAsapBooking = (booking: any) =>
  booking.preferredTime === ASAP_VALUE || booking.preferred_time === ASAP_VALUE;

const isAtOpeningTime = (appointmentTime: any, hours: any, timezone: any) => {
  const appointmentDay = moment(appointmentTime).format('dddd');
  const openingTimeOfAppointmentDay =
    hours && hours[appointmentDay] && hours[appointmentDay][0] && hours[appointmentDay][0].fromTime;

  if (timezone) {
    return moment(appointmentTime).tz(timezone).format('HH:mm:ss') === openingTimeOfAppointmentDay;
  }

  return moment(appointmentTime).format('HH:mm:ss') === openingTimeOfAppointmentDay;
};

const isPremiumBooking = (booking: Booking) => booking?.isPremiumVisit || booking?.is_premium_visit;

const isProviderGroupBooking = (booking: Booking) =>
  !!(booking?.providerId ?? booking?.provider_id);
const getProviderId = (booking: any) => booking.provider_id || booking.providerId;

const isDirectTelemedBooking = (booking: any) =>
  booking.isDirectTelemed || booking.is_direct_telemed;

const isTelemedBooking = (booking: Booking) =>
  !isEmptyString(booking.tokbox_session_id) || !isEmptyString(booking.tokboxSessionId);

const isExternalTelemedBooking = (booking: Booking) =>
  !!(booking.is_external_telemed || booking.isExternalTelemed);

const isInternalOrExternalTelemedBooking = (booking: Booking) => {
  if (isProviderGroupBooking(booking)) {
    return isExternalTelemedBooking(booking);
  }
  return isTelemedBooking(booking);
};

const hasSubmittedRating = (booking: any) =>
  booking && (!!booking.rating_date || !!booking.ratingDate);

const isBookingWithin = (booking: any, amount = 1, unit = 'minutes') =>
  booking &&
  (booking.appointment_date || booking.appointmentDate) &&
  // @ts-expect-error ts-migrate(2769) FIXME: Argument of type 'number' is not assignable to par... Remove this comment to see the full error message
  moment()
    .add(amount, unit)
    .isAfter(booking.appointment_date || booking.appointmentDate);

const isPhotoIdUploadedForBooking = (booking: any) =>
  booking && (booking.hasUploadedPhotoIdFront || booking.hasUploadedPhotoIdBack);

export {
  hasInsuranceInfo,
  isWalkIn,
  isBookingWithin,
  hasSubmittedRating,
  isBookingWidgetBooking,
  isSelfPay,
  isInsuranceSkipped,
  isInsuranceTypeUnset,
  isUpcoming,
  isRescheduledBooking,
  isCallAhead,
  isActiveBooking,
  isCancelled,
  isDischarged,
  isNoShow,
  isRescheduled,
  isOldRescheduledFlow,
  hasBeenTransferred,
  hasRequestedUberRide,
  shouldShowRebookingScreen,
  isAsapBooking,
  isAtOpeningTime,
  isPending,
  isDirectTelemedBooking,
  isTelemedBooking,
  isPremiumBooking,
  isPhotoIdUploadedForBooking,
  isProviderGroupBooking,
  getProviderId,
  isExternalTelemedBooking,
  isInternalOrExternalTelemedBooking,
  isCheckedIn,
};
